import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { Calendar, Export } from '@phosphor-icons/react';
import {
  CreditApplicationStatus,
  getCreditApplicationTypeLabel,
  InternalFormSubmission,
  isCodApplicationType,
  isExternalFormApplicationType,
} from '@dill/dill-shared';
import { useAppDispatch, useAppSelector } from '../../../../../redux/store';
import { getCreditApplications } from '../../../../../redux/services/creditApplicationsService';
import { getInternalFormSubmissions } from '../../../../../redux/services/internalFormsService';
import { getSupplierBranches } from '../../../../../redux/services/supplierAdditionalDetailsService';
import { formatBranchLabel } from '../../../../../utils/helpers';
import { getFormTypeLabel } from '../../../creditApplication/InternalForms/internalFormUtils';
import AppDateRangePickerModal from '../../../../general/AppDateRangePickerModal/AppDateRangePickerModal';
import { DillButton, DillSelect, DillTable } from '../../../../general/Dill';
import type { DillColumn } from '../../../../general/Dill';

type Bucket = 'inProgress' | 'readyForDecision' | 'approved' | 'approvedCod' | 'rejected';

type CountRow = {
  id: string;
  type: string;
  category: 'Credit Application' | 'COD Application' | 'Internal Form' | 'External Form';
  total: number;
} & Record<Bucket, number>;

const BUCKETS: { key: Bucket; label: string }[] = [
  { key: 'inProgress', label: 'In Progress' },
  { key: 'readyForDecision', label: 'Ready for Decision' },
  { key: 'approved', label: 'Approved' },
  { key: 'approvedCod', label: 'Approved as COD' },
  { key: 'rejected', label: 'Rejected' },
];

// Sent / drafted / waiting on signatures or the applicant count as in progress.
const CREDIT_APP_BUCKETS: Record<CreditApplicationStatus, Bucket> = {
  SENT: 'inProgress',
  OPENED: 'inProgress',
  DRAFT: 'inProgress',
  AWAITING_SIGNATURE: 'inProgress',
  INTERNAL_SIGNATURE: 'inProgress',
  FEEDBACK_SENT: 'inProgress',
  APPLICATION_UPDATED: 'inProgress',
  SUBMITTED: 'readyForDecision',
  READY_FOR_DECISION: 'readyForDecision',
  APPROVED: 'approved',
  INTERNALLY_APPROVED: 'approved',
  APPROVED_COD: 'approvedCod',
  INTERNALLY_APPROVED_COD: 'approvedCod',
  REJECTED: 'rejected',
  INTERNALLY_REJECTED: 'rejected',
};

const INTERNAL_FORM_BUCKETS: Record<InternalFormSubmission['status'], Bucket> = {
  SENT: 'inProgress',
  AWAITING_SIGNATURES: 'inProgress',
  SUBMITTED: 'readyForDecision',
  APPROVED: 'approved',
  REJECTED: 'rejected',
};

const emptyCounts = (): Record<Bucket, number> & { total: number } => ({
  total: 0,
  inProgress: 0,
  readyForDecision: 0,
  approved: 0,
  approvedCod: 0,
  rejected: 0,
});

const countColumn = (key: keyof CountRow, label: string, footer: number): DillColumn<CountRow> => ({
  key,
  label,
  width: 130,
  sortable: true,
  numeric: true,
  headerAlign: 'right',
  render: (value: number) => (
    <div
      className={`text-xs text-right tabular-nums ${
        value ? 'font-medium text-gray-700' : 'text-gray-300'
      }`}>
      {value || '—'}
    </div>
  ),
  footer: <div className="text-xs font-semibold text-right tabular-nums">{footer}</div>,
});

// Supplier Reports → Application Counts: how many credit applications, COD applications and
// internal forms of each type were created in a date range, split by where they stand.
const ApplicationCountsReports = () => {
  const dispatch = useAppDispatch();
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const creditApplications = useAppSelector((state) => state.creditApplications.creditApplications);
  const loadingList = useAppSelector((state) => state.creditApplications.loadingList);
  const { creditApplicationTemplates } = useAppSelector(
    (state) => state.creditApplicationTemplates
  );
  const { branches: supplierBranches, loadedSupplierId: loadedSupplierBranchesId } = useAppSelector(
    (state) => state.supplierBranches
  );

  const [internalForms, setInternalForms] = useState<Omit<InternalFormSubmission, 'accessToken'>[]>(
    []
  );
  const [isLoadingInternalForms, setIsLoadingInternalForms] = useState(false);
  const [isDateRangePickerOpen, setIsDateRangePickerOpen] = useState(false);
  const [dateRange, setDateRange] = useState<{ startDate?: Date; endDate?: Date }>({
    startDate: moment().subtract(3, 'months').toDate(),
    endDate: new Date(),
  });
  const [branchId, setBranchId] = useState('');
  const [isExportOpen, setIsExportOpen] = useState(false);

  useEffect(() => {
    if (!currentSupplier?.id) return;
    dispatch(getCreditApplications({ supplierId: currentSupplier.id }));
    setIsLoadingInternalForms(true);
    dispatch(getInternalFormSubmissions({ supplierId: currentSupplier.id })).then((result) => {
      setIsLoadingInternalForms(false);
      if (result.type.includes('fulfilled')) {
        setInternalForms((result as any).payload?.data ?? []);
      }
    });
  }, [currentSupplier?.id, dispatch]);

  useEffect(() => {
    if (currentSupplier?.id && loadedSupplierBranchesId !== currentSupplier.id) {
      dispatch(getSupplierBranches({ supplierId: currentSupplier.id }));
    }
  }, [currentSupplier?.id, loadedSupplierBranchesId, dispatch]);

  const isLoading = loadingList.includes('getCreditApplications') || isLoadingInternalForms;

  const isInRange = (createdAt?: Date | string | null) => {
    if (!createdAt) return false;
    const created = moment(createdAt);
    if (dateRange.startDate && created.isBefore(moment(dateRange.startDate).startOf('day'))) {
      return false;
    }
    if (dateRange.endDate && created.isAfter(moment(dateRange.endDate).endOf('day'))) {
      return false;
    }
    return true;
  };
  const isInBranch = (rowBranchId?: string | null) => !branchId || rowBranchId === branchId;

  const rows = useMemo<CountRow[]>(() => {
    const credit = { id: 'credit', type: 'Credit Applications', ...emptyCounts() };
    const cod = { id: 'cod', type: 'COD Applications', ...emptyCounts() };
    // One row per internal / external form type (template), named as the template was when sent.
    const byTemplate = new Map<string, CountRow>();
    (creditApplications ?? [])
      .filter((app) => !app.isDeleted && isInRange(app.createdAt) && isInBranch(app.branchId))
      .forEach((app) => {
        let target: Record<Bucket, number> & { total: number } = isCodApplicationType(
          app.applicationType
        )
          ? cod
          : credit;
        // Applications made from an external form template get their form's row, as external
        // form submissions do.
        if (isExternalFormApplicationType(app.applicationType)) {
          const key = `external-${app.templateId}`;
          const row = byTemplate.get(key) ?? {
            id: key,
            type: getCreditApplicationTypeLabel(app.applicationType, {
              formName: creditApplicationTemplates.find((t) => t.id === app.templateId)?.name,
            }),
            category: 'External Form' as const,
            ...emptyCounts(),
          };
          byTemplate.set(key, row);
          target = row;
        }
        target.total += 1;
        const bucket = CREDIT_APP_BUCKETS[app.status as CreditApplicationStatus];
        if (bucket) target[bucket] += 1;
      });

    internalForms
      .filter((form) => isInRange(form.createdAt) && isInBranch(form.branchId))
      .forEach((form) => {
        const row = byTemplate.get(form.templateId) ?? {
          id: `internal-${form.templateId}`,
          type: form.templateName || getFormTypeLabel(form),
          category: getFormTypeLabel(form),
          ...emptyCounts(),
        };
        row.total += 1;
        const bucket = INTERNAL_FORM_BUCKETS[form.status];
        if (bucket) row[bucket] += 1;
        byTemplate.set(form.templateId, row);
      });

    return [
      { ...credit, category: 'Credit Application' as const },
      { ...cod, category: 'COD Application' as const },
      ...[...byTemplate.values()].sort((a, b) => a.type.localeCompare(b.type)),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditApplications, creditApplicationTemplates, internalForms, dateRange, branchId]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (sum, row) => {
          (['total', ...BUCKETS.map((b) => b.key)] as (keyof typeof sum)[]).forEach((key) => {
            sum[key] += row[key];
          });
          return sum;
        },
        { ...emptyCounts() }
      ),
    [rows]
  );

  const summary = [
    { label: 'Credit Applications', value: rows[0]?.total ?? 0 },
    { label: 'COD Applications', value: rows[1]?.total ?? 0 },
    {
      label: 'Internal Forms',
      value: rows
        .filter((row) => row.category === 'Internal Form')
        .reduce((s, r) => s + r.total, 0),
    },
    ...(rows.some((row) => row.category === 'External Form')
      ? [
          {
            label: 'External Forms',
            value: rows
              .filter((row) => row.category === 'External Form')
              .reduce((s, r) => s + r.total, 0),
          },
        ]
      : []),
  ];

  const columns: DillColumn<CountRow>[] = [
    {
      key: 'type',
      label: 'Type',
      width: 260,
      sortable: true,
      render: (value: string, row) => (
        <div className="flex flex-col">
          <span className="text-xs font-medium text-gray-800">{value}</span>
          {(row.category === 'Internal Form' || row.category === 'External Form') && (
            <span className="text-[11px] text-gray-400">{row.category}</span>
          )}
        </div>
      ),
      footer: <div className="text-xs font-semibold">Total</div>,
    },
    countColumn('total', 'Total', totals.total),
    ...BUCKETS.map((bucket) => countColumn(bucket.key, bucket.label, totals[bucket.key])),
  ];

  const branchOptions = [
    { label: 'All branches', value: '' },
    ...supplierBranches.map((branch) => ({
      label: formatBranchLabel(branch) || branch.branchId,
      value: branch.branchId,
    })),
  ];

  const formatDateRangeDisplay = () => {
    if (!dateRange.startDate || !dateRange.endDate) return 'Select date range';
    return `${moment(dateRange.startDate).format('MMM DD, YYYY')} - ${moment(
      dateRange.endDate
    ).format('MMM DD, YYYY')}`;
  };

  const exportRows = () =>
    [...rows, { type: 'Total', category: '', ...totals }].map((row) => ({
      Type: row.type,
      Category: row.category,
      Total: row.total,
      ...Object.fromEntries(BUCKETS.map((bucket) => [bucket.label, row[bucket.key]])),
    }));
  const exportFileName = `application-counts-${moment(dateRange.startDate).format(
    'MM-DD-YYYY'
  )}-to-${moment(dateRange.endDate).format('MM-DD-YYYY')}`;

  const handleExportXlsx = () => {
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(exportRows()),
      'Application Counts'
    );
    XLSX.writeFile(workbook, `${exportFileName}.xlsx`);
    setIsExportOpen(false);
  };

  const handleExportCsv = () => {
    const blob = new Blob([Papa.unparse(exportRows())], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${exportFileName}.csv`;
    link.click();
    setIsExportOpen(false);
  };

  return (
    <div className="flex flex-col w-full h-full pt-4">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col gap-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <p className="text-sm text-gray-500 min-w-0 flex-1">
            The number of credit applications, COD applications and each type of internal form
            created in the selected dates, by where they stand. Export to Excel or CSV.
          </p>
          <div className="flex items-center gap-2 shrink-0">
            <div className="min-w-[180px]">
              <DillSelect
                value={branchId}
                options={branchOptions}
                onChange={setBranchId}
                searchable={branchOptions.length > 8}
              />
            </div>
            <button
              onClick={() => setIsDateRangePickerOpen(true)}
              className="inline-flex items-center gap-2 h-[34px] px-3.5 text-xs font-medium rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 cursor-pointer transition-colors whitespace-nowrap">
              <Calendar size={16} weight="bold" />
              <span>{formatDateRangeDisplay()}</span>
            </button>
            <div className="relative">
              <DillButton
                label="Export"
                onClick={() => setIsExportOpen(!isExportOpen)}
                icon={<Export size={14} />}
                variant="primary"
                isDisabled={isLoading}
              />
              {isExportOpen && (
                <div className="absolute right-0 z-50 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 w-40">
                  <button
                    onClick={handleExportXlsx}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                    Export as XLSX
                  </button>
                  <button
                    onClick={handleExportCsv}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                    Export as CSV
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {summary.map((tile) => (
            <div key={tile.label} className="rounded-lg border border-gray-200 px-4 py-3">
              <p className="text-xs text-gray-500">{tile.label}</p>
              <p className="text-2xl font-semibold text-gray-900 tabular-nums">
                {isLoading ? '…' : tile.value}
              </p>
            </div>
          ))}
        </div>

        <DillTable<CountRow>
          columns={columns}
          data={rows}
          rowKey={(row) => row.id}
          verticalDividers
          rowHeight={40}
          striped={false}
          showFooter
          loading={isLoading}
          loadingText="Loading applications..."
          emptyMessage="No applications in these dates"
          showCount={false}
          resizable
        />
      </div>

      <AppDateRangePickerModal
        open={isDateRangePickerOpen}
        handleClose={() => setIsDateRangePickerOpen(false)}
        onSelectDateRange={(range) => {
          setDateRange(range);
          setIsDateRangePickerOpen(false);
        }}
        selectedDateRange={dateRange}
      />
    </div>
  );
};

export default ApplicationCountsReports;
